import { useEffect, useState } from 'react';
import { slugAvailabilitySchema } from '@forecastify/shared';
import { useSession } from '../../session';
import { addressFromName, addressProblem } from './setup-rules';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Is this address free — asked as it is typed, and answered with a way out. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The mockup's `check`: a shape error at once, otherwise *"checking…"* and an answer a beat later;
 * and when the address is refused, a suggestion to click. The mockup knew its taken list; ours asks
 * `GET /setup/slug-available`, which needs no workspace (`0202`), and asks again for each
 * candidate suggestion until one is free.
 *
 * ⚠️ **A late answer for an address no longer in the field is dropped**, by the effect's own
 * cancellation — otherwise typing `kov`, `kova`, `kovacs` quickly could leave the verdict for `kov`
 * on screen under `kovacs`.
 */
export type AddressCheck =
  | { readonly state: 'idle' }
  | { readonly state: 'checking' }
  | { readonly state: 'ok' }
  | {
      readonly state: 'invalid';
      readonly problem: 'chars' | 'hyphen' | 'short';
      readonly suggestion: string | null;
    }
  | { readonly state: 'taken' | 'reserved'; readonly suggestion: string | null };

const PAUSE_MS = 400;

/**
 * @param own the workspace's current address when step 2 is reopened to edit it — free by
 *   definition, and `/setup/slug-available` (which has no workspace to exclude) would call it taken.
 */
export function useAddressCheck(
  slug: string,
  workspaceName: string,
  own: string | null = null,
): AddressCheck {
  const session = useSession();
  const [check, setCheck] = useState<AddressCheck>({ state: 'idle' });

  useEffect(() => {
    /* An object, so the cleanup's write is visible across the awaits — a `let` reads as `true`. */
    const alive = { current: true };
    /* A call, not a read: after one check TypeScript narrows the property for the rest of the
       function, across every `await` where the cleanup may have run. */
    const stillHere = (): boolean => alive.current;

    const isFree = async (candidate: string): Promise<'free' | 'taken' | 'reserved' | null> => {
      const response = await session
        .request(`/setup/slug-available?slug=${encodeURIComponent(candidate)}`)
        .catch(() => null);
      if (response?.ok !== true) return null;
      const parsed = slugAvailabilitySchema.safeParse(await response.json().catch(() => null));
      if (!parsed.success) return null;
      return parsed.data.available ? 'free' : (parsed.data.reason ?? 'taken');
    };

    /* The first free candidate that is not what is already in the field. */
    const suggest = async (base: string): Promise<string | null> => {
      const fromName = addressFromName(workspaceName);
      const candidates = [fromName, base, `${base}-2`, `${base}-3`].filter(
        (candidate, index, all) =>
          candidate !== '' &&
          candidate !== slug &&
          all.indexOf(candidate) === index &&
          addressProblem(candidate) === null,
      );
      for (const candidate of candidates) {
        if ((await isFree(candidate)) === 'free') return candidate;
        if (!stillHere()) return null;
      }
      return null;
    };

    if (slug === '') {
      setCheck({ state: 'idle' });
      return;
    }
    if (own !== null && slug === own) {
      setCheck({ state: 'ok' });
      return;
    }

    const problem = addressProblem(slug);
    if (problem !== null) {
      setCheck({ state: 'invalid', problem, suggestion: null });
      void suggest(addressFromName(slug)).then((suggestion) => {
        if (stillHere()) setCheck({ state: 'invalid', problem, suggestion });
      });
      return () => {
        alive.current = false;
      };
    }

    setCheck({ state: 'checking' });
    const timer = setTimeout(() => {
      void (async () => {
        const verdict = await isFree(slug);
        if (!stillHere()) return;
        if (verdict === 'free') {
          setCheck({ state: 'ok' });
          return;
        }
        /* ⚠️ An unreachable API is not "taken": the field stays `checking`, the button stays off
           with *"the address is being checked"*, and the next keystroke asks again. */
        if (verdict === null) return;
        setCheck({ state: verdict, suggestion: null });
        const suggestion = await suggest(slug);
        if (stillHere()) setCheck({ state: verdict, suggestion });
      })();
    }, PAUSE_MS);

    return () => {
      alive.current = false;
      clearTimeout(timer);
    };
    /* ⚠️ `workspaceName` is here for the suggestion it seeds. While the address follows the name the
       two change together anyway; once somebody has typed their own address, a change to the name
       costs one more debounced question, which is cheaper than a stale suggestion. */
  }, [slug, workspaceName, own, session]);

  return check;
}
